import React, { useMemo, useState } from 'react';
import { Briefcase, Loader2 } from 'lucide-react';
import { JobModalShell, primaryBtnStyle, secondaryBtnStyle } from './JobModalShell';
import { JOB_TYPES, STAGES, T, toDateInput } from '../constants';

const inputStyle = (invalid) => ({
  width: '100%', padding: '9px 12px', boxSizing: 'border-box',
  border: `1.5px solid ${invalid ? T.brand : T.line}`, borderRadius: 10,
  fontSize: 13.5, color: T.ink, fontFamily: T.font, outline: 'none',
  background: T.surface, transition: 'border-color 0.15s, box-shadow 0.15s',
});

const Field = ({ label, required, error, children, span = 1 }) => (
  <div style={{ gridColumn: `span ${span}`, minWidth: 0 }}>
    <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: T.body, marginBottom: 5 }}>
      {label}{required && <span style={{ color: T.brand }}> *</span>}
    </label>
    {children}
    {error && <div style={{ fontSize: 11, color: T.brand, marginTop: 4, fontWeight: 600 }}>{error}</div>}
  </div>
);

const EMPTY = {
  title: '', company: '', stage: 'applied', location: '', url: '', salary: '',
  jobType: '', appliedDate: '', interviewDate: '',
  contactName: '', contactEmail: '', contactPhone: '', notes: '',
};

/** Add / edit an application. `job` present means edit. */
export const JobFormModal = ({ job, defaultStage = 'applied', saving, onSave, onClose }) => {
  const isEdit = Boolean(job);

  const [form, setForm] = useState(() => (job ? {
    ...EMPTY,
    ...job,
    appliedDate: toDateInput(job.appliedDate),
    interviewDate: toDateInput(job.interviewDate),
  } : {
    ...EMPTY,
    stage: defaultStage,
    // Default to today for a real application; a bookmark has not been sent yet.
    appliedDate: defaultStage === 'bookmarked' ? '' : toDateInput(new Date()),
  }));

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.title.trim())   next.title = 'Job title is required.';
    if (!form.company.trim()) next.company = 'Company is required.';
    if (form.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.contactEmail.trim())) {
      next.contactEmail = 'Enter a valid email address.';
    }
    if (form.url.trim() && /\s/.test(form.url.trim())) next.url = 'URLs cannot contain spaces.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    try {
      await onSave({
        ...form,
        title: form.title.trim(),
        company: form.company.trim(),
      });
    } catch (err) {
      setSubmitError(err.message || 'Could not save this application.');
    }
  };

  const stageOptions = useMemo(() => STAGES.map(s => ({ value: s.id, label: s.label })), []);

  return (
    <JobModalShell
      title={isEdit ? 'Edit Application' : 'Add Job'}
      subtitle={isEdit ? 'Update the details of this application.' : 'Track a new application through your pipeline.'}
      icon={Briefcase}
      onClose={onClose}
      maxWidth={620}
      footer={
        <>
          <button type="button" onClick={onClose} style={secondaryBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.background = T.canvas; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.background = T.surface; }}>
            Cancel
          </button>
          <button type="submit" form="jt-job-form" disabled={saving} style={primaryBtnStyle(saving)}>
            {saving && <Loader2 size={14} style={{ animation: 'jtSpin 1s linear infinite' }} />}
            {isEdit ? 'Save Changes' : 'Add Job'}
          </button>
        </>
      }
    >
      <style>{`@keyframes jtSpin{to{transform:rotate(360deg)}}`}</style>

      <form id="jt-job-form" onSubmit={handleSubmit} noValidate>
        <div className="jt-form-grid">
          <Field label="Job title" required error={errors.title} span={2}>
            <input value={form.title} onChange={set('title')} placeholder="e.g. Frontend Developer"
              style={inputStyle(errors.title)} autoFocus
              onFocus={e => { e.target.style.borderColor = T.brand; e.target.style.boxShadow = '0 0 0 3px rgba(239,29,40,0.10)'; }}
              onBlur={e => { e.target.style.borderColor = errors.title ? T.brand : T.line; e.target.style.boxShadow = 'none'; }} />
          </Field>

          <Field label="Company" required error={errors.company}>
            <input value={form.company} onChange={set('company')} placeholder="e.g. Google"
              style={inputStyle(errors.company)} />
          </Field>

          <Field label="Stage" required>
            <select value={form.stage} onChange={set('stage')} style={inputStyle(false)}>
              {stageOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>

          <Field label="Location">
            <input value={form.location} onChange={set('location')} placeholder="e.g. London, UK" style={inputStyle(false)} />
          </Field>

          <Field label="Job type">
            <select value={form.jobType} onChange={set('jobType')} style={inputStyle(false)}>
              {JOB_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>

          <Field label="Salary">
            <input value={form.salary} onChange={set('salary')} placeholder="e.g. £55,000" style={inputStyle(false)} />
          </Field>

          <Field label="Job URL" error={errors.url}>
            <input value={form.url} onChange={set('url')} placeholder="https://…" style={inputStyle(errors.url)} />
          </Field>

          <Field label="Application date">
            <input type="date" value={form.appliedDate} onChange={set('appliedDate')} style={inputStyle(false)} />
          </Field>

          <Field label="Interview date">
            <input type="date" value={form.interviewDate} onChange={set('interviewDate')} style={inputStyle(false)} />
          </Field>

          <Field label="Recruiter / contact">
            <input value={form.contactName} onChange={set('contactName')} placeholder="Name" style={inputStyle(false)} />
          </Field>

          <Field label="Contact email" error={errors.contactEmail}>
            <input value={form.contactEmail} onChange={set('contactEmail')} placeholder="name@company.com"
              style={inputStyle(errors.contactEmail)} />
          </Field>

          <Field label="Contact phone" span={2}>
            <input value={form.contactPhone} onChange={set('contactPhone')} placeholder="Optional" style={inputStyle(false)} />
          </Field>

          <Field label="Notes" span={2}>
            <textarea value={form.notes} onChange={set('notes')} rows={3}
              placeholder="Interview prep, referral name, follow-up reminders…"
              style={{ ...inputStyle(false), resize: 'vertical', lineHeight: 1.55 }} />
          </Field>
        </div>

        {submitError && (
          <div style={{
            marginTop: 14, background: T.brandSoft, border: `1px solid ${T.brandBorder}`,
            borderRadius: 10, padding: '9px 13px', fontSize: 12.5, color: T.brandDark, fontWeight: 600,
          }}>
            {submitError}
          </div>
        )}
      </form>
    </JobModalShell>
  );
};
